import clsx from 'clsx'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { trackOfferPageTopCtaClick } from 'components/OfferPage/analytics'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import { NavWrapper } from 'components/OfferPage/styles'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  arePropertyRatesLoading,
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyCheapestRates,
  getPropertyFromDurationLabel,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowAnytimeRate } from 'selectors/offerPage/propertyOfferPageSelectors'

const Container = styled(NavWrapper)`
  &.hideOnMobile.hideOnMobile {
    display: none;
    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  flex-wrap: wrap;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    margin-inline-start: auto;
  }

  > * + * {
    margin-inline-start: ${rem(12)};
  }
`

const MobileNewLine = styled.br`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  margin-inline-start: auto;
  justify-content: flex-end;
  position: relative;

  & > * + * {
    margin-inline-start: ${rem(8)};
  }
`
const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-inline-start: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

interface Props {
  property: App.Property.Property
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  headlessMode: boolean
  durationLabel: string
  availableForPurchase?: boolean
  cheapestPropertyRates: [App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined, App.EnrichedPropertyRate | undefined]
  cheapestHotelOnlyDiscountRate: App.EnrichedPropertyRate | undefined
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
}

function PropertyStickyHeader(props: Props) {
  const {
    property,
    offer,
    onViewPackageOptions,
    areAnytimeRates,
    shouldShowAnytimeRate,
    isLoading,
    headlessMode,
    durationLabel,
    availableForPurchase,
    cheapestPropertyRates,
    cheapestHotelOnlyDiscountRate,
    checkIn,
    checkOut,
    rooms,
  } = props

  const intl = useIntl()
  const [cheapestHotelOnlyRate] = cheapestPropertyRates

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate
  const hasHotelPrice = !hideAnytimePrices && !!cheapestHotelOnlyRate

  const hotelPrice = cheapestHotelOnlyRate?.totals.displayPrice ?? 0
  const memberPrice = cheapestHotelOnlyRate?.luxPlusTotals?.displayPrice ?? 0
  const isBedbankRate = cheapestHotelOnlyRate && 'service' in cheapestHotelOnlyRate && cheapestHotelOnlyRate.service.type === 'bedbank'
  // Discount only when the displayed rate is the anchored rate. A price match alone is unsafe: a
  // different rate can share the price, and the rate list keys the discount by rateId — so it would
  // show here but attach to another package on "view options".
  const shouldApplyDiscount = isBedbankRate &&
    cheapestHotelOnlyDiscountRate &&
    cheapestHotelOnlyDiscountRate.service.propertyId === cheapestHotelOnlyRate.service.propertyId &&
    cheapestHotelOnlyDiscountRate.rateId === cheapestHotelOnlyRate.rateId
  const [discountAmount, discountFetching] = useMetasearchDiscount(hotelPrice, {
    propertyId: isBedbankRate ? cheapestHotelOnlyRate.service.propertyId : undefined,
    checkIn,
    checkOut,
    rooms,
    disabled: !shouldApplyDiscount,
  })
  const displayHotelPrice = Math.max(hotelPrice - discountAmount, 0)
  const displayMemberPrice = memberPrice ? Math.max(memberPrice - discountAmount, 0) : memberPrice

  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    trackOfferPageTopCtaClick()
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }
  const displaySoldOutLabel = !availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED
  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice = !!displayMemberPrice && displayMemberPrice < displayHotelPrice

  return (
    <Container className={clsx({ hideOnMobile: hideSoldOutOnMobile })}>
      <Root>
        <div>
          {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
        </div>
        {availableForPurchase && (
          <WalledContent enforced={property.walledGarden} wall={<PriceWrapper><GenericLoginSignupWall /></PriceWrapper>}>
            <PriceWrapper>
              {hasHotelPrice && hotelPrice > 0 && <Group direction="vertical" horizontalAlign="end">
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <LuxPlusPriceGuard
                  showMemberPrice={showMemberPrice}
                  fallbackPrice={<PriceRowPrice
                    data-testid="property-sticky-header-price"
                    size="S"
                    price={displayHotelPrice}
                    saleUnit="total"
                  />}
                >
                  <PriceRowLuxPlusPrice
                    size="M"
                    memberPrice={displayMemberPrice}
                    saleUnit="total"
                    logoPlacement="leading"
                  />
                </LuxPlusPriceGuard>
                <LoadingIndicator visible={isLoading || discountFetching} floating opaque />
              </Group>}
              <TextButton kind="primary" data-testid="offer-view-options" onClick={onViewOptionsClick}>
                <CSSBreakpoint max="tablet" as="span">
                  {intl.formatMessage({
                    id: 'lIrp9DNfphqQDJ9365rN',
                    defaultMessage: 'View options',
                    description: 'Button text for mobile view to view package options on property sticky header',
                  })}
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop" as="span">
                  {intl.formatMessage({
                    id: 'Q6ZdGCm1IYrvKc52baKc',
                    defaultMessage: 'View package options',
                    description: 'Button text for desktop view to view package options on property sticky header',
                  })}
                </CSSBreakpoint>
              </TextButton>
            </PriceWrapper>
          </WalledContent>
        )}
        {!availableForPurchase && displaySoldOutLabel &&
          <InlineWrapper>
            <BodyText variant="medium" weight="bold" colour="normal-contrast">
              <FormattedMessage
                id="Ciqto2iTzNKZjdENkEhO"
                defaultMessage="This offer has{lineBreak} SOLD OUT"
                description="Sold out message on property sticky header with line break for mobile"
                values={{
                  lineBreak: <MobileNewLine />,
                }}
              />
            </BodyText>
            <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
              {intl.formatMessage(commonMessages.browseAllHotelOffers)}
            </TextButton>
          </InlineWrapper>}
      </Root>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowAnytimeRate: shouldShowAnytimeRate(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  headlessMode: appState.system.headlessMode,
  durationLabel: getPropertyFromDurationLabel(state, appState),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  cheapestHotelOnlyDiscountRate: getPropertyCheapestHotelOnlyRateForDiscount(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  rooms: state.rooms,
  open: !!state.activeFilter,
}), PropertyStickyHeader, preventRenderWithOpenFilter)
